/*
 * heatmapRenderer.js
 *
 * Portions based on simpleheat by Vladimir Agafonkin
 * https://github.com/mourner/simpleheat
 *
 * Copyright (c) 2014, Vladimir Agafonkin
 * ISC License: https://opensource.org/licenses/ISC
 */

import { DISPLAY_MODES } from './displayModes';

// ガウシアン減衰係数: 中心からの距離に応じた強度の減衰速度を制御する。
// 値が小さいほど赤（高強度）の領域が広くなる。
export const GAUSSIAN_DECAY = 3;

// opacity比例化のガンマ補正値（低強度領域の視認性向上）
const GAMMA_CORRECTION = 0.4;

// 比較パレットの中央インデックス（白/変化なし）
const PALETTE_MIDPOINT = 128;
// パレット中央から端までの正側ステップ数（128〜255 → 127段階）
const PALETTE_POSITIVE_RANGE = 127;
// パレット中央から端までの負側ステップ数（128〜0 → 128段階）
const PALETTE_NEGATIVE_RANGE = 128;

/**
 * 生の捕獲数を0〜1の強度に変換する。
 * 平方根スケールを使用することで、低捕獲数の視認性を確保しつつ
 * 高捕獲数域のグラデーション解像度を維持する。
 */
export function sqrtIntensity(value, sqrtMax) {
  return sqrtMax > 0
    ? Math.min(Math.sqrt(Math.abs(value)) / sqrtMax, 1.0)
    : 0;
}

/**
 * 低強度領域の視認性を上げるガンマ補正付きアルファ値を返す。
 * GAMMA_CORRECTION < 1 により、低い intensity でもアルファが底上げされ、
 * 捕獲数が少ないエリアも薄く着色される。
 */
export function applyGammaAlpha(intensity, finalAlpha) {
  return Math.round(intensity ** GAMMA_CORRECTION * finalAlpha);
}

/**
 * ガウシアン減衰に基づくグラデーションストップを生成する。
 * 中心(offset=0)で最大、境界(offset=1)で0に減衰するα値の配列を返す。
 * @param {number} numStops - 生成するストップ数（デフォルト: 5）
 * @returns {Array<{offset: number, alpha: number}>} offsetとalphaのペア配列
 */
export function buildGradientStops(numStops = 5) {
  const stops = [];
  for (let i = 0; i < numStops; i++) {
    const offset = i / (numStops - 1);
    const alpha = offset >= 1.0 ? 0 : Math.exp(-GAUSSIAN_DECAY * offset * offset);
    stops.push({ offset, alpha });
  }
  return stops;
}

// Canvas API非依存: 数学的RGB線形補間
export function buildPalette(colorStops) {
  const palette = new Array(256);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let lower = colorStops[0];
    let upper = colorStops[colorStops.length - 1];
    for (let j = 0; j < colorStops.length - 1; j++) {
      if (t >= colorStops[j].stop && t <= colorStops[j + 1].stop) {
        lower = colorStops[j];
        upper = colorStops[j + 1];
        break;
      }
    }
    const range = upper.stop - lower.stop;
    const localT = range === 0 ? 0 : (t - lower.stop) / range;
    palette[i] = [
      Math.round(lower.color[0] + (upper.color[0] - lower.color[0]) * localT),
      Math.round(lower.color[1] + (upper.color[1] - lower.color[1]) * localT),
      Math.round(lower.color[2] + (upper.color[2] - lower.color[2]) * localT),
      255,
    ];
  }
  return palette;
}

export const PALETTES = {
  heat: buildPalette([
    { stop: 0.0, color: [0, 0, 255] },
    { stop: 0.25, color: [0, 255, 0] },
    { stop: 0.5, color: [255, 255, 0] },
    { stop: 1.0, color: [255, 0, 0] },
  ]),
  comparison: buildPalette([
    { stop: 0.0, color: [49, 54, 149] },
    { stop: 0.25, color: [145, 191, 219] },
    { stop: 0.5, color: [255, 255, 255] },
    { stop: 0.75, color: [223, 136, 104] },
    { stop: 1.0, color: [180, 4, 38] },
  ]),
};

function createBrush(radius) {
  const brush = document.createElement('canvas');
  const diameter = radius * 2;
  brush.width = diameter;
  brush.height = diameter;
  const ctx = brush.getContext('2d');

  const gradient = ctx.createRadialGradient(radius, radius, 0, radius, radius, radius);
  buildGradientStops().forEach(({ offset, alpha }) => {
    gradient.addColorStop(offset, `rgba(0,0,0,${alpha})`);
  });

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, diameter, diameter);
  return brush;
}

/**
 * 2Dガウシアンカーネルを事前計算する。
 * renderComparisonでは正負値の分離が必要なため、Canvas APIのglobalAlpha合成が
 * 使えず、ピクセル単位で手動畳み込みを行う。このカーネルはその重み行列。
 */
function buildGaussianKernel(radius) {
  const kernelRadius = Math.ceil(radius);
  const kernelSize = 2 * kernelRadius + 1;
  const kernel = new Float32Array(kernelSize * kernelSize);
  for (let ky = 0; ky < kernelSize; ky++) {
    for (let kx = 0; kx < kernelSize; kx++) {
      const dx = kx - kernelRadius;
      const dy = ky - kernelRadius;
      const dist = Math.sqrt(dx * dx + dy * dy) / radius;
      kernel[ky * kernelSize + kx] = dist <= 1.0 ? Math.exp(-GAUSSIAN_DECAY * dist * dist) : 0;
    }
  }
  return { kernel, kernelRadius, kernelSize };
}

/**
 * 各データポイントの影響をカーネルで拡散し、正負それぞれのバッファに蓄積する。
 * 正(増加)と負(減少)を分離するのは、同一ピクセルに正負が重なった場合に
 * 優勢側の色で表示するため（colorizeComparisonで判定）。
 */
function accumulateBuffers(dataPoints, options) {
  const { posBuffer, negBuffer, kernel, kernelRadius, kernelSize, width, height, sqrtMax } = options;
  dataPoints.forEach(({ x, y, value }) => {
    const magnitude = sqrtIntensity(value, sqrtMax);
    const cx = Math.round(x);
    const cy = Math.round(y);
    const target = value >= 0 ? posBuffer : negBuffer;

    for (let ky = 0; ky < kernelSize; ky++) {
      const py = cy - kernelRadius + ky;
      if (py >= 0 && py < height) {
        for (let kx = 0; kx < kernelSize; kx++) {
          const px = cx - kernelRadius + kx;
          if (px >= 0 && px < width) {
            const kernelVal = kernel[ky * kernelSize + kx];
            if (kernelVal > 0) {
              target[py * width + px] += magnitude * kernelVal;
            }
          }
        }
      }
    }
  });
}

/**
 * Canvas合成で得られたα値をパレットインデックスとして色変換する。
 * α値が高い＝ブラシが重なった＝強度が高い、という対応関係を利用している。
 */
export function colorizeHeatmap(imageData, { palette, finalAlpha }) {
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const alpha = pixels[i + 3];
    if (alpha > 0) {
      const clampedAlpha = Math.min(alpha, 255);
      const [r, g, b] = palette[clampedAlpha];
      pixels[i] = r;
      pixels[i + 1] = g;
      pixels[i + 2] = b;
      pixels[i + 3] = applyGammaAlpha(clampedAlpha / 255, finalAlpha);
    }
  }
}

/**
 * 正負バッファからダイバージングカラーマップに変換する。
 * パレットは0(青/減少)〜128(白/変化なし)〜255(赤/増加)の構成。
 * 各ピクセルで正負の優勢側を採用し、そのインデックスで色を決定する。
 */
function colorizeComparison(imageData, { posBuffer, negBuffer, palette, finalAlpha }) {
  const pixels = imageData.data;
  const bufferSize = posBuffer.length;

  for (let i = 0; i < bufferSize; i++) {
    const pos = Math.min(posBuffer[i], 1.0);
    const neg = Math.min(negBuffer[i], 1.0);
    if (pos > 0 || neg > 0) {
      let paletteIdx;
      if (pos >= neg) {
        paletteIdx = PALETTE_MIDPOINT + Math.round(pos * PALETTE_POSITIVE_RANGE);
      } else {
        paletteIdx = PALETTE_MIDPOINT - Math.round(neg * PALETTE_NEGATIVE_RANGE);
      }
      paletteIdx = Math.max(0, Math.min(paletteIdx, 255));

      const pi = i * 4;
      const [r, g, b] = palette[paletteIdx];
      pixels[pi] = r;
      pixels[pi + 1] = g;
      pixels[pi + 2] = b;
      pixels[pi + 3] = applyGammaAlpha(Math.max(pos, neg), finalAlpha);
    }
  }
}

export function buildDataPoints(traps, displayMode) {
  return traps.map(trap => ({
    x: parseFloat(trap.coordinates.x),
    y: parseFloat(trap.coordinates.y),
    value: displayMode === DISPLAY_MODES.COMPARISON_HEAT
      ? trap.count - (trap.previousMonthCount ?? 0)
      : trap.count,
  }));
}

export function comparisonMaxValue(dataPoints) {
  return Math.max(...dataPoints.map(d => Math.abs(d.value)), 1);
}

export class HeatmapRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
  }

  resize(width, height) {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  /** 通常ヒートマップ: Canvas APIのブラシ合成 → パレット色変換 */
  render(dataPoints, { palette, radius, opacity = 0.8, maxValue }) {
    this.clear();
    if (!dataPoints.length) return;

    const brush = createBrush(radius);
    const { ctx } = this;
    const sqrtMax = Math.sqrt(maxValue);

    dataPoints.forEach(({ x, y, value }) => {
      ctx.globalAlpha = sqrtIntensity(value, sqrtMax);
      ctx.drawImage(brush, x - radius, y - radius);
    });

    const imageData = ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    colorizeHeatmap(imageData, {
      palette, finalAlpha: Math.round(255 * opacity),
    });
    ctx.putImageData(imageData, 0, 0);
  }

  /** 比較ヒートマップ: 手動カーネル畳み込み（正負分離） → ダイバージングカラー変換 */
  renderComparison(dataPoints, { palette, radius, opacity = 0.6, maxValue }) {
    this.clear();
    if (!dataPoints.length) return;

    const { width, height } = this.canvas;
    const posBuffer = new Float32Array(width * height);
    const negBuffer = new Float32Array(width * height);
    const { kernel, kernelRadius, kernelSize } = buildGaussianKernel(radius);
    const sqrtMax = Math.sqrt(maxValue);

    accumulateBuffers(dataPoints, {
      posBuffer, negBuffer, kernel, kernelRadius, kernelSize, width, height, sqrtMax,
    });

    const imageData = this.ctx.createImageData(width, height);
    colorizeComparison(imageData, {
      posBuffer, negBuffer, palette, finalAlpha: Math.round(255 * opacity),
    });

    this.ctx.putImageData(imageData, 0, 0);
  }

  renderFromTraps(traps, displayMode, { radius, circleScaleMax }) {
    const dataPoints = buildDataPoints(traps, displayMode);

    if (displayMode === DISPLAY_MODES.COMPARISON_HEAT) {
      this.renderComparison(dataPoints, {
        palette: PALETTES.comparison,
        radius,
        maxValue: comparisonMaxValue(dataPoints),
      });
    } else {
      this.render(dataPoints, {
        palette: PALETTES.heat,
        radius,
        maxValue: circleScaleMax,
      });
    }
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
